import React, { useCallback, useEffect, useState } from 'react';
import { DealsApiService } from '@vanilla/services/crm/DealsApiService.js';
import { ActivityService } from '@vanilla/services/ActivityService.js';
import { formatDateDisplay } from '@vanilla/utils/Utils.js';
import { ToastManager } from '@/lib/toast.js';
import { Badge, statusBadgeVariant } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { confirmAlert } from '@/lib/confirmAlert';
import NextStepFormDialog from './NextStepFormDialog.jsx';
import TaskFormDialog from './TaskFormDialog.jsx';

function parseMentions(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

/**
 * Sidebar Activities card: Next Steps + Tasks (SoT DealFormManager).
 */
export default function DealSidebarActivities({ dealId, users = [], initialTab = null, highlightId = null }) {
    const [tab, setTab] = useState(() => (initialTab === 'tasks' ? 'tasks' : 'nextsteps'));
    const [nextSteps, setNextSteps] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [showAllNs, setShowAllNs] = useState(false);
    const [showAllTasks, setShowAllTasks] = useState(false);
    const [modal, setModal] = useState(null);

    useEffect(() => {
        if (initialTab !== 'tasks' && initialTab !== 'nextsteps') return;
        setTab(initialTab);
        const t = setTimeout(() => {
            const wrappers = [
                document.getElementById('dealRightSidebar'),
                document.querySelector('.deal-details-sheet-body'),
                document.querySelector('.deal-form-right-sidebar'),
            ].filter(Boolean);
            const targetId = initialTab === 'tasks' ? 'sidebarTasksContent' : 'sidebarNextStepsContent';
            const el = document.getElementById(targetId);
            const card = el?.closest('.sidebar-card') || document.querySelector('#dealRightSidebar .sidebar-card') || document.querySelector('.deal-details-sheet-body .sidebar-card');
            if (card) card.classList.remove('collapsed');
            if (el) {
                el.closest('.sidebar-card')?.classList.remove('collapsed');
                wrappers.forEach((w) => {
                    const c = el.closest('.sidebar-card');
                    if (c && w.contains(c)) w.scrollTo({ top: c.offsetTop - 12, behavior: 'smooth' });
                });
                requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
            } else if (card) {
                wrappers.forEach((w) => { if (w.contains(card)) w.scrollTo({ top: card.offsetTop - 12, behavior: 'smooth' }); });
            }
        }, 420);
        return () => clearTimeout(t);
    }, [initialTab, dealId]);

    useEffect(() => {
        if (!highlightId) return;
        const t = setTimeout(() => {
            const isTask = tasks.some((t) => String(t.id) === String(highlightId));
            const isNs = nextSteps.some((s) => String(s.id) === String(highlightId));
            if (isTask) setTab('tasks');
            if (isNs) setTab('nextsteps');
            requestAnimationFrame(() => {
                const el = document.querySelector(`[data-activity-id="${highlightId}"]`) || document.getElementById(highlightId);
                if (el) {
                    const container = el.closest('#sidebarTasksContent') || el.closest('#sidebarNextStepsContent') || el.closest('.deal-form-right-sidebar') || el.closest('.deal-details-sheet-body') || document.getElementById('dealRightSidebar');
                    if (container) {
                        const cRect = container.getBoundingClientRect();
                        const eRect = el.getBoundingClientRect();
                        const offset = eRect.top - cRect.top + container.scrollTop - (cRect.height / 2) + (eRect.height / 2);
                        try { container.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' }); } catch {}
                    } else {
                        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    }
                    el.classList.add('ring-2', 'ring-primary', 'rounded');
                    setTimeout(() => el.classList.remove('ring-2', 'ring-primary', 'rounded'), 2500);
                }
            });
        }, 600);
        return () => clearTimeout(t);
    }, [highlightId, nextSteps, tasks, dealId]);

    const loadNextSteps = useCallback(async () => {
        if (!dealId) {
            setNextSteps([]);
            return [];
        }
        try {
            const res = await DealsApiService.getNextSteps(dealId);
            const rows = Array.isArray(res) ? res : (res?.data || []);
            const mapped = rows.map((s) => ({
                id: s.id,
                action: s.action,
                date: s.dueDate || s.due_date || '',
                status: s.is_completed === 'true' || s.is_completed === true ? 'done' : 'pending',
                sortOrder: s.sortOrder ?? s.sort_order ?? 0,
            }));
            setNextSteps(mapped);
            return mapped;
        } catch (err) {
            console.warn('[DealSidebarActivities] next steps failed', err);
            setNextSteps([]);
            return [];
        }
    }, [dealId]);

    const loadTasks = useCallback(async () => {
        if (!dealId) {
            setTasks([]);
            return [];
        }
        try {
            const rows = await ActivityService.getForEntity('deal', dealId, {
                perPage: 200,
                sortOrder: 'desc',
                activityType: 'task',
            });
            const mapped = (rows || []).map((t) => {
                const mentions = parseMentions(t.mentions);
                return {
                    id: t.id,
                    subject: t.subject || '',
                    assignedTo: mentions[0] || '',
                    dueDate: t.dueDate || '',
                    status: t.is_completed === 'true' || t.completed ? 'done' : 'pending',
                };
            });
            setTasks(mapped);
            return mapped;
        } catch (err) {
            console.warn('[DealSidebarActivities] tasks failed', err);
            setTasks([]);
            return [];
        }
    }, [dealId]);

    useEffect(() => {
        loadNextSteps();
        loadTasks();
    }, [loadNextSteps, loadTasks]);

    const notifyInsights = (ns = nextSteps, ts = tasks) => {
        if (!dealId) return;
        const pendingNextSteps = (ns || []).filter((s) => s.status !== 'done');
        const firstPending = pendingNextSteps[0] || (ns || [])[0];
        const nextStep = firstPending?.action || '';
        const pendingNextStepCount = pendingNextSteps.length;
        const pendingTaskCount = (ts || []).filter((t) => t.status !== 'done').length;
        window.dispatchEvent(new CustomEvent('dealActivitiesChanged', {
            detail: {
                dealId,
                nextStep,
                nextSteps: [...(ns || [])],
                pendingNextStepCount,
                pendingTaskCount,
            },
        }));
    };

    const openAddNextStep = () => {
        if (!dealId) {
            ToastManager.show({ title: 'Next step', message: 'Save the deal before adding next steps.', tone: 'warning' });
            return;
        }
        setModal({ type: 'add-ns' });
    };

    const openEditNextStep = (index) => {
        const item = nextSteps[index];
        if (!item) return;
        setModal({ type: 'edit-ns', item });
    };

    const openAddTask = () => {
        if (!dealId) {
            ToastManager.show({ title: 'Task', message: 'Save the deal before adding tasks.', tone: 'warning' });
            return;
        }
        setModal({ type: 'add-task' });
    };

    const openEditTask = (index) => {
        const item = tasks[index];
        if (!item) return;
        setModal({ type: 'edit-task', item });
    };

    const toggleNextStep = async (index) => {
        const item = nextSteps[index];
        if (!item) return;
        const newStatus = item.status === 'done' ? 'pending' : 'done';
        const next = nextSteps.map((s, i) => (i === index ? { ...s, status: newStatus } : s));
        setNextSteps(next);
        notifyInsights(next, tasks);
        if (dealId && item.id) {
            try {
                await DealsApiService.updateNextStep(dealId, item.id, {
                    is_completed: newStatus === 'done' ? 'true' : 'false',
                });
            } catch (e) {
                console.warn('Failed to update next step', e);
                loadNextSteps();
            }
        }
    };

    const deleteNextStep = async (index) => {
        const item = nextSteps[index];
        if (!item) return;
        const ok = await confirmAlert({
            title: 'Delete next step',
            message: 'Delete this next step?',
            confirmLabel: 'Delete',
            confirmClass: 'btn-danger',
        });
        if (!ok) return;
        const next = nextSteps.filter((_, i) => i !== index);
        setNextSteps(next);
        notifyInsights(next, tasks);
        if (dealId && item.id) {
            try {
                await DealsApiService.deleteNextStep(dealId, item.id);
            } catch (e) {
                console.warn('Failed to delete next step', e);
                loadNextSteps();
            }
        }
    };

    const toggleTask = async (index) => {
        const item = tasks[index];
        if (!item?.id) return;
        const newStatus = item.status === 'done' ? 'pending' : 'done';
        const next = tasks.map((t, i) => (i === index ? { ...t, status: newStatus } : t));
        setTasks(next);
        try {
            await ActivityService.toggleTask(item.id, newStatus === 'done');
            notifyInsights(nextSteps, next);
        } catch (e) {
            console.warn('Failed to toggle task', e);
            loadTasks();
        }
    };

    const deleteTask = async (index) => {
        const item = tasks[index];
        if (!item) return;
        const ok = await confirmAlert({
            title: 'Delete task',
            message: 'Delete this task?',
            confirmLabel: 'Delete',
            confirmClass: 'btn-danger',
        });
        if (!ok) return;
        const next = tasks.filter((_, i) => i !== index);
        setTasks(next);
        if (item.id) {
            try {
                await ActivityService.deleteTask(item.id);
                notifyInsights(nextSteps, next);
            } catch (e) {
                console.warn('Failed to delete task', e);
                loadTasks();
            }
        }
    };

    const userName = (id) => {
        const u = users.find((x) => String(x.id) === String(id));
        return u ? (u.name || u.email || u.id) : '';
    };

    const nsVisible = showAllNs ? nextSteps : nextSteps.slice(0, 3);
    const taskVisible = showAllTasks ? tasks : tasks.slice(0, 3);

    return (
        <div className="sidebar-card">
            <div className="sidebar-card-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Activities
            </div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList unstyled className="sidebar-tabs" id="sidebarActivityTabs">
                    <TabsTrigger unstyled className="sidebar-tab" value="nextsteps" data-tab="nextsteps">
                        Next Steps
                    </TabsTrigger>
                    <TabsTrigger unstyled className="sidebar-tab" value="tasks" data-tab="tasks">
                        Tasks
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {tab === 'nextsteps' && (
                <div id="sidebarNextStepsContent">
                    <div className="activity-list" id="sidebarNextStepList">
                        {!dealId && <div className="activity-empty">Save the deal to manage next steps</div>}
                        {dealId && nextSteps.length === 0 && <div className="activity-empty">No next steps yet</div>}
                        {nsVisible.map((s, i) => {
                            const idx = nextSteps.indexOf(s);
                            return (
                                <div className="activity-list-item" key={s.id || `ns-${i}`} data-activity-id={s.id}>
                                    <div className="activity-list-item-head">
                                        <div className="activity-list-item-title">{s.action}</div>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <button type="button" className="link-btn" onClick={() => openEditNextStep(idx)}>Edit</button>
                                            <button type="button" className="link-btn link-danger" onClick={() => deleteNextStep(idx)}>Delete</button>
                                        </div>
                                    </div>
                                    <div className="activity-list-item-meta">
                                        {s.date ? <span>Due: {formatDateDisplay(s.date)}</span> : null}
                                        <Badge variant={statusBadgeVariant(s.status)} className={`activity-status-badge ${s.status || 'pending'}`}>{s.status || 'pending'}</Badge>
                                    </div>
                                    <div className="activity-list-item-actions">
                                        {s.status === 'done' ? (
                                            <button type="button" className="activity-action-btn is-pending" onClick={() => toggleNextStep(idx)}>Reopen</button>
                                        ) : (
                                            <button type="button" className="activity-action-btn is-done" onClick={() => toggleNextStep(idx)}>Done</button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {nextSteps.length > 3 && (
                            <button type="button" className="activity-load-more" onClick={() => setShowAllNs((v) => !v)}>
                                {showAllNs ? 'Show less' : `Load more (${nextSteps.length - 3} more)`}
                            </button>
                        )}
                    </div>
                    <button type="button" className="premium-add-btn" id="sidebarAddNextStepBtn" onClick={openAddNextStep}>
                        + Add Next Step
                    </button>
                </div>
            )}

            {tab === 'tasks' && (
                <div id="sidebarTasksContent">
                    <div className="activity-list" id="sidebarTaskList">
                        {!dealId && <div className="activity-empty">Save the deal to manage tasks</div>}
                        {dealId && tasks.length === 0 && <div className="activity-empty">No tasks yet</div>}
                        {taskVisible.map((t, i) => {
                            const idx = tasks.indexOf(t);
                            const assignee = userName(t.assignedTo);
                            return (
                                <div className="activity-list-item" key={t.id || `task-${i}`} data-activity-id={t.id}>
                                    <div className="activity-list-item-head">
                                        <div className="activity-list-item-title">{t.subject}</div>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <button type="button" className="link-btn" onClick={() => openEditTask(idx)}>Edit</button>
                                            <button type="button" className="link-btn link-danger" onClick={() => deleteTask(idx)}>Delete</button>
                                        </div>
                                    </div>
                                    <div className="activity-list-item-meta">
                                        <span>Due: {t.dueDate ? formatDateDisplay(String(t.dueDate).slice(0, 10)) : '—'}</span>
                                        {assignee ? <span>{assignee}</span> : null}
                                        <Badge variant={statusBadgeVariant(t.status)} className={`activity-status-badge ${t.status || 'pending'}`}>{t.status || 'pending'}</Badge>
                                    </div>
                                    <div className="activity-list-item-actions">
                                        {t.status === 'done' ? (
                                            <button type="button" className="activity-action-btn is-pending" onClick={() => toggleTask(idx)}>Reopen</button>
                                        ) : (
                                            <button type="button" className="activity-action-btn is-done" onClick={() => toggleTask(idx)}>Done</button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {tasks.length > 3 && (
                            <button type="button" className="activity-load-more" onClick={() => setShowAllTasks((v) => !v)}>
                                {showAllTasks ? 'Show less' : `Load more (${tasks.length - 3} more)`}
                            </button>
                        )}
                    </div>
                    <button type="button" className="premium-add-btn" id="sidebarAddTaskBtn" onClick={openAddTask}>
                        + Add Task
                    </button>
                </div>
            )}

            <NextStepFormDialog
                open={modal?.type === 'add-ns' || modal?.type === 'edit-ns'}
                mode={modal?.type === 'edit-ns' ? 'edit' : 'add'}
                dealId={dealId}
                initial={modal?.type === 'edit-ns' ? modal.item : null}
                sortOrder={nextSteps.length}
                onClose={() => setModal(null)}
                onSaved={async () => {
                    const ns = await loadNextSteps();
                    notifyInsights(ns, tasks);
                }}
            />

            <TaskFormDialog
                open={modal?.type === 'add-task' || modal?.type === 'edit-task'}
                mode={modal?.type === 'edit-task' ? 'edit' : 'add'}
                dealId={dealId}
                initial={modal?.type === 'edit-task' ? modal.item : null}
                users={users}
                onClose={() => setModal(null)}
                onSaved={async () => {
                    const ts = await loadTasks();
                    notifyInsights(nextSteps, ts);
                }}
            />
        </div>
    );
}
